import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Heading, Text, Flex, IconButton, Badge } from '@radix-ui/themes';
import { ArrowLeftIcon } from '@radix-ui/react-icons';

interface DetailPageLayoutProps {
  /** The path to navigate back to */
  backPath: string;
  /** The text to show in the back button */
  backText: string;
  /** The icon to display (React element) */
  icon: React.ReactNode;
  /** The background color for the icon box (e.g., 'var(--purple-3)') */
  iconBackgroundColor: string;
  /** The icon color (e.g., 'var(--purple-11)') */
  iconColor: string;
  /** The main title of the page */
  title: string;
  /** Optional subtitle/description */
  subtitle?: string | null;
  /** Optional badge to show next to title */
  badge?: React.ReactNode;
  /** Optional action buttons to show on the right */
  actions?: React.ReactNode;
  /** The main content of the page */
  children: React.ReactNode;
}

const DetailPageLayout: React.FC<DetailPageLayoutProps> = ({
  backPath,
  backText,
  icon,
  iconBackgroundColor,
  iconColor,
  title,
  subtitle,
  badge,
  actions,
  children,
}) => {
  const navigate = useNavigate();

  return (
    <Box px="1">
      {/* Header */}
      <Flex direction="column" py="2" gap="2">
        <Flex align="start" justify="between">
          <IconButton
            variant="ghost"
            color="gray"
            onClick={() => navigate(backPath)}
            style={{ display: 'flex', gap: '4px' }}
          >
            <Flex align="center" gap="2">
              <ArrowLeftIcon width={20} height={20} />
              <Text size="2" color="gray">
                {backText}
              </Text>
            </Flex>
          </IconButton>
        </Flex>
        <Flex align="start" justify="between" my="2">
          <Flex align="center" gap="3">
            <Box
              style={{
                padding: '8px',
                borderRadius: '8px',
                backgroundColor: iconBackgroundColor,
                color: iconColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {icon}
            </Box>
            <Box>
              <Flex align="center" gap="2">
                <Heading as="h1" size="5" weight="bold">
                  {title}
                </Heading>
                {badge}
              </Flex>
              {subtitle && (
                <Text size="2" color="gray">
                  {subtitle}
                </Text>
              )}
            </Box>
          </Flex>
          <Flex style={{ alignSelf: 'center' }}>
            {actions}
          </Flex>
        </Flex>
      </Flex>

      {/* Content */}
      {children}
    </Box>
  );
};

export default DetailPageLayout;
